import React from 'react';
import { Check, Pencil } from 'lucide-react';
import { resolveImageUrl } from '../../lib/imageCache';
import { formatPrice } from '../../lib/utils';

// Sticky header for the step list: "N of M steps done" progress, early price estimate and pinned fabric choice.
// Compact single-row layout on phones so it never hides the steps underneath.
export const ConfiguratorProgress = ({ steps, openSections, onJump, fabric, onChangeFabric, estimate, roomMissing }) => {
  const done = steps.filter(s => s.complete).length;
  const onlyRoomLeft = roomMissing && done === steps.length - 1;
  const allDone = done === steps.length || onlyRoomLeft;
  const pct = Math.round((done / steps.length) * 100);
  const path = fabric ? [fabric.brand, fabric.collection, fabric.material].filter(Boolean) : [];
  const priceText = estimate?.amount > 0 ? formatPrice(estimate.amount) : null;
  const label = allDone
    ? (roomMissing ? 'All steps done — choose a room to add to cart' : 'All steps done — your price is ready')
    : `${done} of ${steps.length} steps done`;

  return (
    <div className="sticky top-20 z-20 -mx-2 px-2 pt-2 pb-2 sm:pb-3 mb-3 bg-white/95 backdrop-blur-sm border-b border-neutral-200" data-testid="configurator-progress">
      <div className="flex items-center justify-between gap-3 mb-1.5 sm:mb-2">
        <p className="text-xs sm:text-sm font-medium text-neutral-900 truncate" data-testid="progress-label">
          <span className="sm:hidden">{allDone ? (roomMissing ? 'Done — pick a room' : 'Done') : `${done}/${steps.length} steps`}</span>
          <span className="hidden sm:inline">{label}</span>
        </p>
        <span className="text-xs shrink-0 text-right">
          {priceText ? (
            <span className={estimate.isEstimate ? 'text-amber-700' : 'text-neutral-900 font-medium'} data-testid="progress-price" data-estimate={estimate.isEstimate ? 'true' : 'false'}>
              {estimate.isEstimate ? `from ${priceText}` : priceText}
              <span className="hidden sm:inline text-neutral-500 font-normal">{estimate.isEstimate ? ' · estimate' : ' · total'}</span>
            </span>
          ) : (
            <span className="text-neutral-500">{allDone ? '100%' : `${pct}%`}<span className="hidden sm:inline"> · {allDone ? 'complete' : `${steps.length - done} to go for a price`}</span></span>
          )}
        </span>
      </div>
      <div className="flex gap-1" role="list">
        {steps.map((s) => {
          const open = openSections.includes(s.id);
          return (
            <button
              key={s.id}
              type="button"
              role="listitem"
              onClick={() => onJump(s.id)}
              title={`${s.title}${s.complete ? ' · done' : ''}`}
              className={`group relative h-1.5 sm:h-2 flex-1 rounded-full transition-colors ${s.complete ? 'bg-amber-500 hover:bg-amber-600' : open ? 'bg-neutral-400' : 'bg-neutral-200 hover:bg-neutral-300'} ${open ? 'ring-2 ring-offset-1 ring-neutral-900/60' : ''}`}
              data-testid={`progress-step-${s.id}`}
              data-complete={s.complete ? 'true' : 'false'}
            >
              <span className="pointer-events-none absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-neutral-900 px-1.5 py-0.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100 hidden sm:block">{s.title}</span>
            </button>
          );
        })}
      </div>
      {fabric?.color && (
        <button
          type="button"
          onClick={onChangeFabric}
          className="mt-2 sm:mt-3 w-full flex items-center gap-2 sm:gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1.5 sm:px-3 sm:py-2 text-left hover:border-neutral-400 transition-colors"
          data-testid="sticky-fabric-summary"
        >
          <span className="w-7 h-7 sm:w-9 sm:h-9 rounded-md overflow-hidden border border-black/10 shrink-0 bg-neutral-200">
            {fabric.color.image_url
              ? <img src={resolveImageUrl(fabric.color.image_url, 128)} alt={fabric.color.name} className="w-full h-full object-cover" />
              : <span className="block w-full h-full" style={{ backgroundColor: fabric.color.hex_color || '#e5e5e5' }} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="hidden sm:flex text-[10px] uppercase tracking-wider text-neutral-500 items-center gap-1"><Check className="w-3 h-3 text-amber-600" /> Your fabric</span>
            <span className="block text-xs sm:text-sm text-neutral-900 truncate" data-testid="sticky-fabric-path">
              <span className="hidden sm:inline">{path.map((p, i) => <React.Fragment key={i}>{p} <span className="text-neutral-400">›</span> </React.Fragment>)}</span>
              <span className="font-medium">{fabric.color.name}</span>
              {fabric.color.code && <span className="text-neutral-400 font-mono text-xs"> · {fabric.color.code}</span>}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-neutral-500 shrink-0"><Pencil className="w-3 h-3" /> <span className="hidden sm:inline">Change</span></span>
        </button>
      )}
    </div>
  );
};
